import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GraduationCap, Lock, Loader2, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { apiService } from '../services/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkToken = async () => {
      console.log('ResetPasswordPage: Checking token:', token);
      if (token) {
        try {
          console.log('ResetPasswordPage: Calling verifyResetToken API...');
          const response = await apiService.verifyResetToken(token);
          console.log('ResetPasswordPage: API response:', response);
          setTokenValid(true);
        } catch (err) {
          console.error('ResetPasswordPage: API error:', err);
          console.error('ResetPasswordPage: Error response:', err.response);
          setError(err.response?.data?.detail || 'Invalid or expired reset token');
        } finally {
          setVerifying(false);
        }
      } else {
        console.log('ResetPasswordPage: No token provided');
        setVerifying(false);
        setError('No reset token provided');
      }
    };
    
    checkToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await apiService.resetPassword(token, newPassword);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="login-page" data-testid="reset-password-page">
        <div className="login-container">
          <div className="login-card">
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B] mb-4" />
              <p className="text-[#595959]">Verifying reset token...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (error && !tokenValid) {
    return (
      <div className="login-page" data-testid="reset-password-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="login-title">Invalid Reset Link</h1>
              <p className="login-subtitle">{error}</p>
            </div>

            <div className="space-y-4 pt-4">
              <p className="text-[#595959] text-center text-sm">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              
              <div className="flex flex-col gap-3">
                <Link to="/forgot-password">
                  <Button className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl">
                    Request New Reset Link
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full rounded-xl border-[#E5E5E5]">
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="login-page" data-testid="reset-password-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="login-title">Password Reset!</h1>
              <p className="login-subtitle">Your password has been changed successfully</p>
            </div>

            <div className="space-y-4 pt-4">
              <p className="text-[#595959] text-center text-sm">
                You can now sign in with your new password.
              </p>
              
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="login-page" data-testid="reset-password-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <GraduationCap size={28} />
            </div>
            <h1 className="login-title">Create New Password</h1>
            <p className="login-subtitle">Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} data-testid="reset-password-form">
            <div className="form-group">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  data-testid="new-password-input"
                  className="pl-10 pr-10 rounded-xl border-[#E5E5E5] focus:border-[#2C4C3B] focus:ring-[#2C4C3B]/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#595959]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-[#595959] mt-1">Must be at least 8 characters</p>
            </div>

            <div className="form-group">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                  className="pl-10 rounded-xl border-[#E5E5E5] focus:border-[#2C4C3B] focus:ring-[#2C4C3B]/30"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          <div className="login-footer">
            <Link 
              to="/login" 
              className="text-[#2C4C3B] hover:underline text-sm font-medium"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
